import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Field, reduxForm } from 'redux-form';
import { SubmissionError } from 'redux-form';
import { formatValidationErrors } from '../../common/utils/getApiReducer';
import ReduxFormsImageCropper from '../../common/forms/ReduxFormsImageCropper';
import ReduxFormsPlacesAutocomplete from '../../common/forms/ReduxFormsPlacesAutocomplete';
import CropperScaffold from '../../common/forms/CropperScaffold';
import cloneDeep from 'lodash/cloneDeep';
import ReduxFormInput from '../../common/forms/ReduxFormInput';
import getDecodedToken from '../../common/auth/utils/getDecodedToken';
import { updateMovementSpecialistProfile } from './redux/actions';
import dataUrlToFile from '../../common/utils/dataUrlToFile';
import { pushToast } from '../../redux/actions';
import StripeExpress from './StripeExpress';

import 'cropperjs/dist/cropper.css';

const validate = values => {
  const errors = {};
  const { availability, biography, location, name, profile_image, profile_image_id } = values;

  if (!availability || !availability.trim()) {
    errors.availability = ['Please enter your availability'];
  } else if (availability.trim().length > 255) {
    errors.availability = ['Availability cannot be longer than 255 characters'];
  }

  if (!biography || !biography.trim()) {
    errors.biography = ['Please enter a biography'];
  }

  if (!location || !location.location_long) {
    errors.location = ['Please select a location'];
  }

  if (!name || !name.trim()) {
    errors.name = ['Please enter a name'];
  }

  if (!profile_image && !profile_image_id) {
    errors.profile_image = ['Please upload a profile image'];
  }

  return errors;
};

class ProfileEditForm extends Component {
  state = {
    clearOrigProfileImage: false,
    clearOrigHeaderImage: false,
  };

  onSubmit = values => {
    const { dispatch } = this.props;
    const submitValues = cloneDeep(values);

    submitValues.availability = submitValues.availability.trim();
    submitValues.biography = submitValues.biography.trim();
    submitValues.name = submitValues.name.trim();

    if (typeof submitValues.location === 'object') {
      submitValues.latitude = submitValues.location.latitude;
      submitValues.longitude = submitValues.location.longitude;
      submitValues.location_short = submitValues.location.location_short;
      submitValues.location_long = submitValues.location.location_long;
    }

    if (submitValues.profile_image) {
      submitValues.profile_image = dataUrlToFile(
        submitValues.profile_image.imageResult,
        submitValues.profile_image.imageFilename
      );
    }

    if (submitValues.header_image) {
      submitValues.header_image = dataUrlToFile(
        submitValues.header_image.imageResult,
        submitValues.header_image.imageFilename
      );
    }

    if (!this.state.clearOrigProfileImage) {
      submitValues.profile_image_id = this.props.profile.get('profile_image_id');
    }

    if (!this.state.clearOrigHeaderImage) {
      submitValues.header_image_id = this.props.profile.get('header_image_id');
    }

    return dispatch(updateMovementSpecialistProfile(getDecodedToken().sub, submitValues)).then(action => {
      window.scrollTo(0, 0);
      if (!action.response.ok) {
        dispatch(
          pushToast({
            message: 'There was an error when trying to save your profile.',
            status: 'error',
          })
        );
        throw new SubmissionError(formatValidationErrors(action.json).toJS());
      } else {
        dispatch(pushToast({ message: 'Your profile has been saved.' }));
        this.setState({
          clearOrigProfileImage: false,
          clearOrigHeaderImage: false,
        });
      }
    });
  };

  clearProfileImage = () => {
    this.setState({ clearOrigProfileImage: true });
  };

  clearHeaderImage = () => {
    this.setState({ clearOrigHeaderImage: true });
  };

  render() {
    const { handleSubmit, profile, submitting } = this.props;

    const { clearOrigProfileImage, clearOrigHeaderImage } = this.state;

    return (
      <div>
        <div className="ms-admin">
          <h3>Movement Specialist Admin Profile</h3>
          <form onSubmit={handleSubmit(this.onSubmit)}>
            <div className="ms-admin__inputs">
              <div className="ms-admin__input">
                <Field name="name" component={ReduxFormInput} label="Name">
                  <input type="text" />
                </Field>
              </div>
            </div>
            <div className="ms-admin__images">
              {profile && profile.get('profile_image_id') && !clearOrigProfileImage ? (
                <CropperScaffold label="Profile Image" required>
                  <div
                    className="original-image"
                    style={{ backgroundImage: `url(${profile.getIn(['profileImage', 'location'])})` }}
                  />
                  <div className="cropper-scaffold__controls">
                    <button type="button" className="button button--theme--outline" onClick={this.clearProfileImage}>
                      Remove Profile Image
                    </button>
                  </div>
                </CropperScaffold>
              ) : (
                <Field name="profile_image" component={ReduxFormsImageCropper} label="Profile Image" />
              )}
              {profile && profile.get('header_image_id') && !clearOrigHeaderImage ? (
                <CropperScaffold label="Background Header Image (Optional)" required>
                  <div
                    className="original-image"
                    style={{ backgroundImage: `url(${profile.getIn(['headerImage', 'location'])})` }}
                  />
                  <div className="cropper-scaffold__controls">
                    <button type="button" className="button button--theme--outline" onClick={this.clearHeaderImage}>
                      Remove Header Image
                    </button>
                  </div>
                </CropperScaffold>
              ) : (
                <Field
                  name="header_image"
                  component={ReduxFormsImageCropper}
                  label="Background Header Image (Optional)"
                />
              )}
            </div>
            <div className="ms-admin__inputs">
              <div className="ms-admin__inputs__info">
                <Field
                  name="location"
                  component={ReduxFormsPlacesAutocomplete}
                  label="Location"
                  initialValue={profile.get('location_long')}
                />
                <Field name="availability" component={ReduxFormInput} label="Availability">
                  <input type="text" />
                </Field>
              </div>
              <div className="ms-admin__inputs__bio">
                <Field name="biography" component={ReduxFormInput} label="Biography">
                  <textarea />
                </Field>
              </div>
            </div>
            <div className="ms-admin__button-row">
              <StripeExpress />
              <button type="submit" className="button" disabled={submitting}>
                Save
              </button>
            </div>
            {profile.get('stripe_express_account_id') ? null : (
              <p className="disclaimer">
                Stripe Express is the platform used to manage payouts from your sessions. You must register before you
                can begin booking sessions.
              </p>
            )}
          </form>
        </div>
      </div>
    );
  }
}

const mapStateToProps = state => {
  const profile = state.subscription.user.getIn(['data', 'movementSpecialistProfile']);
  const initialValues = {
    availability: profile.get('availability'),
    biography: profile.get('biography'),
    latitude: profile.get('latitude'),
    location: {
      location_long: profile.get('location_long'),
      location_short: profile.get('location_short'),
      longitude: profile.get('longitude'),
      latitude: profile.get('latitude'),
    },
    longitude: profile.get('longitude'),
    name: profile.get('name'),
  };

  return {
    initialValues,
    profile,
  };
};

export default connect(mapStateToProps)(
  reduxForm({
    form: 'msProfileEditForm',
    onSubmitFail: () => {
      window.scrollTo(0, 0);
    },
    validate,
  })(ProfileEditForm)
);



// WEBPACK FOOTER //
// ./src/subscription/movement-specialist-admin/ProfileEditForm.js